import moment, { Moment } from 'moment';
import * as React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { DateRange } from '@mui/lab';
import { CalendarDaysIcon } from '@heroicons/react/20/solid';
import { Tab } from './Tab';
import type { Value } from './Tabs';

interface Props {
  minStartDate?: moment.Moment;
  value: Value;
  onChange: (value: Value) => void;
}

export const DateRangeSelector: React.FC<Props> = (props) => {
  const { minStartDate, value, onChange } = props;

  const dateArray: DateRange<Moment> = [
    value.dateRange.startDate ? moment(value.dateRange.startDate) : null,
    value.dateRange.endDate ? moment(value.dateRange.endDate) : null,
  ];

  const [open, setOpen] = React.useState(false);
  const tabIsActive = open || value.custom || false;

  return (
    <>
      <Tab
        active={tabIsActive}
        value=""
        label={
          <div className="flex items-center gap-2">
            <CalendarDaysIcon className="h-4 w-4" />
            <span>Custom</span>
          </div>
        }
        onChange={() => setOpen(!open)}
      />
      <LocalizationProvider id="localization" dateAdapter={AdapterDayjs}>
        <DateRangePicker
          minDate={minStartDate}
          value={dateArray}
          calendars={1}
          disableFuture
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onChange={(range: DateRange<any>) => {
            if (range) {
              // MUI adapter uses a weird version of moment that doesn't work as expected
              const date = range.map((d) => (d ? moment(d.toISOString()) : null));

              // This additional logic handles the edge case of a pre-existing start date
              // without an end date; typically the component will default to re-selecting
              // the start date in this situation instead of the end date as expected
              if (value[0] && !value[1] && !date[1]) {
                // If the chosen date is after the start date, set the end date otherwise
                // set the start date
                if (moment(date[0]).isAfter(value[0])) {
                  onChange({
                    dateRange: {
                      exactDate: date[0],
                    },
                    custom: true,
                  });
                } else {
                  onChange({
                    dateRange: {
                      exactDate: date[0],
                    },
                    custom: true,
                  });
                }
              } else {
                onChange({
                  dateRange: {
                    startDate: date[0],
                    endDate: date[1] || date[0],
                  },
                  custom: true,
                });
              }
            }
          }}
          renderInput={() => <div />}
          open={open}
        />
      </LocalizationProvider>
    </>
  );
};
DateRangeSelector.displayName = 'DateRangeSelector';
